/**
 * @file CouponFormScreen.tsx
 *
 * Routed wrapper around {@link CouponForm} for the `/coupons/new` and
 * `/coupons/:id/edit` routes. Resolves the seed coupon for edit via the store
 * (cache-first, then fetch-by-id so a direct link or refresh still works), shows
 * loading/error states, and owns persistence: it calls the store's create/update,
 * toasts the save outcome, and navigates back to the list on success. A failed
 * save surfaces inline (in addition to the error toast) — a known `duplicate_code`
 * / `invalid_amount` failure is mapped to a field error in the form, anything else
 * shows a generic banner; the seed-load error only
 * applies before the seed has loaded, so a save failure never flips the form to
 * the load-error screen.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { useCouponsStore } from '../../stores/coupons.store';
import { useSettingsStore } from '../../stores/settings.store';
import type { ShopCouponPayload } from '../../api/shop-types';
import { EmptyState } from '../../components/EmptyState';
import { CouponForm, type CouponFormMode } from './CouponForm';

interface CouponFormScreenProps {
  mode: CouponFormMode;
}

/**
 * Renders the routed coupon form for the given mode.
 *
 * @param props - The form mode (create/edit).
 *
 * @returns The coupon form screen (with loading/error fallbacks).
 */
export function CouponFormScreen({ mode }: CouponFormScreenProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();

  const current = useCouponsStore((s) => s.current);
  const formLoading = useCouponsStore((s) => s.formLoading);
  const formError = useCouponsStore((s) => s.formError);
  const loadOne = useCouponsStore((s) => s.loadOne);
  const submitCreate = useCouponsStore((s) => s.submitCreate);
  const submitUpdate = useCouponsStore((s) => s.submitUpdate);
  const resetForm = useCouponsStore((s) => s.resetForm);

  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [serverErrorCode, setServerErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'create') {
      resetForm();
    } else if (id !== undefined) {
      loadOne(id);
    }
    return () => resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  const handleSubmit = async (payload: ShopCouponPayload): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    setServerErrorCode(null);
    const result =
      mode === 'edit' && id !== undefined
        ? await submitUpdate(id, payload)
        : await submitCreate(payload);
    setSaving(false);
    if (!result) {
      notifyError(intl.formatMessage(adminMessages.toastError));
      const code = useCouponsStore.getState().formErrorCode;
      if (
        code === 'coupon_code_conflict' ||
        code === 'coupon_invalid_amount' ||
        code === 'coupon_invalid_window' ||
        code === 'coupon_invalid_spend'
      ) {
        setServerErrorCode(code);
      } else {
        setSaveError(intl.formatMessage(adminMessages.couponsFormSaveError));
      }
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastSaved));
    navigate('/coupons');
  };

  const goBack = (): void => {
    navigate('/coupons');
  };

  // The seed is ready when creating, or when the edit target has loaded. Until
  // then the edit screen shows the loader (or the load-error if the fetch failed);
  // once ready, the form stays mounted so a save failure surfaces inline.
  const seedReady = mode === 'create' || (current !== null && current.id === id);

  if (mode === 'edit' && !seedReady) {
    if (formError) {
      return (
        <EmptyState
          title={intl.formatMessage(adminMessages.couponsFormLoadErrorTitle)}
          description={intl.formatMessage(adminMessages.couponsFormLoadErrorDescription)}
          action={{
            label: intl.formatMessage(adminMessages.couponsTitle),
            onClick: goBack,
          }}
        />
      );
    }
    return (
      <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
        <Loader>
          <FormattedMessage {...adminMessages.couponsFormLoading} />
        </Loader>
      </div>
    );
  }

  return (
    <CouponForm
      mode={mode}
      coupon={mode === 'create' ? undefined : (current ?? undefined)}
      currencySymbol={currencySymbol}
      saving={saving}
      saveError={saveError}
      serverErrorCode={serverErrorCode}
      onClearServerError={() => setServerErrorCode(null)}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
